import React, { useEffect, useRef, useState } from "react";

/**
 * Animated number counter that runs when it enters the viewport.
 * Accepts a `value` like "14+", "50+", "2.4x" and counts the numeric portion.
 */
export default function Counter({ value, duration = 1600, className = "" }) {
  const [display, setDisplay] = useState("0");
  const ref = useRef(null);
  const didRun = useRef(false);

  useEffect(() => {
    const match = String(value).match(/([\d.]+)/);
    const target = match ? parseFloat(match[1]) : 0;
    const suffix = match ? String(value).slice(match.index + match[1].length) : String(value);
    const prefix = match ? String(value).slice(0, match.index) : "";
    const isFloat = String(target).includes(".");

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !didRun.current) {
            didRun.current = true;
            const start = performance.now();
            const tick = (t) => {
              const p = Math.min(1, (t - start) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              const cur = target * eased;
              setDisplay(prefix + (isFloat ? cur.toFixed(1) : Math.floor(cur).toString()) + suffix);
              if (p < 1) requestAnimationFrame(tick);
              else setDisplay(prefix + (isFloat ? target.toFixed(1) : String(target)) + suffix);
            };
            requestAnimationFrame(tick);
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [value, duration]);

  return <span ref={ref} className={className}>{display}</span>;
}
